import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TablePagination,
  TableContainer,
  Divider,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import api from "../../utils/api";

export default function StudentPaymentPage() {
	  // Router
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState({
    cbt: null,
    post_utme: null,
  });
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    (async () => {
      try {
        const [subRes, payRes] = await Promise.all([
          api.get("/subscription/me"),
          api.get("/payments/me"),
        ]);

        setSubscription(subRes.data);
        setPayments(payRes.data || []);
      } catch (err) {
        console.error(err);
        setError("Failed to load subscription information.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const paginatedPayments = payments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const renderSubscriptionCard = (title, data, color) => (
    <Paper
      sx={{
        p: 3,
        height: "100%",
        borderRadius: 2,
        border: data ? `1px solid ${color}` : "1px dashed #ccc",
      }}
    >
      <Stack spacing={1}>
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>

        {!data ? (
          <>
            <Chip label="No Active Plan" color="error" size="small" />
            <Typography variant="body2" color="text.secondary">
              You do not have an active {title} subscription.
            </Typography>
          </>
        ) : (
          <>
            <Chip label="Active" color="success" size="small" />
            <Typography variant="body2">
              Plan: <b>{data.plan}</b>
            </Typography>
            <Typography variant="body2">
              Expires in <b>{data.days_remaining} days</b>
            </Typography>
            <Typography variant="body2">
              Start: {new Date(data.start_date).toLocaleDateString()}
            </Typography>
            <Typography variant="body2">
              Expiry: {new Date(data.expiry_date).toLocaleDateString()}
            </Typography>
          </>
        )}
      </Stack>
    </Paper>
  );

  return (
    <Box>
      {/* HEADER */}
      <Typography
        variant="h5"
        fontWeight={700}
        sx={{ mb: 0.5, fontSize: { xs: "1.3rem", md: "1.6rem" } }}
      >
        💳 My Subscription
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3, maxWidth: 600 }}
      >
        View your subscription status, payment history, and renew plans.
      </Typography>

      {/* SUBSCRIPTION CARDS */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          {renderSubscriptionCard("CBT", subscription.cbt, "#1976d2")}
        </Grid>

        <Grid item xs={12} md={6}>
          {renderSubscriptionCard("POST UTME", subscription.post_utme, "#2e7d32")}
        </Grid>
      </Grid>

      {/* CTA */}
     <Button
  variant="contained"
  startIcon={<AutorenewIcon />}
  sx={{ mb: 4 }}
  onClick={() => navigate("/dashboard/pricing")}
>
  Manage / Subscribe
</Button>
      <Divider sx={{ mb: 3 }} />

      {/* PAYMENT HISTORY */}
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
        <Typography
          variant="h6"
          fontWeight={600}
          sx={{ mb: 2, fontSize: { xs: "1rem", md: "1.2rem" } }}
        >
          🧾 Payment History
        </Typography>

        {payments.length === 0 ? (
          <Typography>No payment records found.</Typography>
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedPayments.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        {p.date
                          ? new Date(p.date).toLocaleDateString()
                          : "-"}
                      </TableCell>
                      <TableCell>{p.plan}</TableCell>
                      <TableCell>₦{p.amount}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.reference}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={p.status}
                          color={p.status === "success" ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={payments.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
